import { useEffect, useState } from 'react';
import axios from 'axios';
import { Gavel, ChevronRight, Scale } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { DEFAULT_PROJECT_ID } from '@/lib/navTree';

const API = process.env.REACT_APP_BACKEND_URL;
const nav = (section) => window.dispatchEvent(new CustomEvent('fbc-nav', { detail: section }));

// One-line pointer to the single home of corpus law, method & calculator (Post-Possession Rights → Corpus Fund).
export const CorpusRightsLink = ({ testid = 'corpus-rights-link' }) => (
  <button type="button" data-testid={testid}
    onClick={() => { sessionStorage.setItem('ppr_tab', 'corpus'); nav('post-possession-rights'); }}
    className="w-full text-left rounded-lg border border-amber-300 bg-amber-50/60 dark:bg-amber-950/20 px-3 py-2 hover:bg-amber-100/60 transition-colors flex items-center gap-2">
    <Scale className="h-4 w-4 text-amber-700 shrink-0" />
    <span className="text-sm font-medium flex-1">Your corpus rights &amp; calculator</span>
    <ChevronRight className="h-4 w-4" />
  </button>
);

// Live summary read from the Corpus Issues room's own records (no copy kept here).
export const CorpusLiveCard = () => {
  const [pulse, setPulse] = useState(undefined);
  useEffect(() => {
    axios.get(`${API}/api/corpus-warroom/${DEFAULT_PROJECT_ID}/pulse`)
      .then((r) => setPulse(r.data)).catch(() => setPulse(null));
  }, []);
  if (pulse === undefined) return null;
  return (
    <button type="button" onClick={() => nav('corpus-warroom')} data-testid="corpus-live-card"
      className="w-full text-left rounded-lg border border-emerald-300 bg-emerald-50/50 dark:bg-emerald-950/20 p-3 hover:bg-emerald-100/50 transition-colors">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-800 flex items-center gap-1"><Gavel className="h-3.5 w-3.5" /> Live corpus matter at TEN BKC</p>
      {pulse === null ? (
        <p className="text-sm mt-1">Open to verified TEN BKC members. Open the Corpus Issues room →</p>
      ) : !pulse.active ? (
        <p className="text-sm mt-1" data-testid="corpus-live-none">No corpus matter recorded for this project yet.</p>
      ) : (
        <div className="mt-1 space-y-1">
          <p className="text-sm font-semibold" data-testid="corpus-live-title">{pulse.title || 'Corpus Protection Case'}</p>
          {pulse.latest_event && <p className="text-xs text-muted-foreground">Latest: {pulse.latest_event}{pulse.latest_event_date ? ` (${pulse.latest_event_date})` : ''}</p>}
          <div className="flex items-center gap-2">
            {pulse.stage && <Badge variant="outline" className="text-[10px]">{pulse.stage}</Badge>}
            <span className="text-xs text-muted-foreground">{pulse.docs_count} document(s)</span>
            <span className="text-xs font-medium text-emerald-800 ml-auto">Open the Corpus Issues room →</span>
          </div>
        </div>
      )}
    </button>
  );
};

export const CorpusPrinciple = () => (
  <div className="rounded-lg border border-amber-200 dark:border-amber-900/40 bg-amber-50/30 dark:bg-amber-900/10 p-4 space-y-2" data-testid="corpus-principle">
    <p className="text-sm leading-relaxed">
      <span className="font-semibold">The principle.</span>{' '}
      Corpus is 100% the buyers&apos; money. It is collected by the developer in trust
      and must flow intact to the society on the buyers&apos; admission as members.
      Deductions referencing the developer&apos;s separate arrangements with original
      society members are not, in law, the free-sale buyer&apos;s obligation.
    </p>
    <p className="text-xs text-muted-foreground">
      Anchor precedents: <em>Signia Pearl (NCLT, BKC), Vasathi Anandi / Shree Sapta CHS (NCLT Mumbai), Eden Garden CHS (NCDRC).</em>
    </p>
  </div>
);
